import React, { useState } from 'react';
import css from './SportsPage.module.css';
import { H2, LayoutSingleColumn, OutsideClickHandler, Page } from '../../components';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { sportsOptions } from '../../config/configExtras';
import classNames from 'classnames';
import IconArrowTop from '../../components/IconArrowTop/IconArrowTop';
import AutoSuggestion from './AutoSuggestion';
import { isArrayLength } from '../../util/genericHelpers';
import { FilterOptionsKeys, UserType } from '../../util/enums';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const SportsPage = () => {
  const { ALL, OTHER, WOMEN, MEN } = FilterOptionsKeys;
  const intl = useIntl();
  const history = useHistory();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const schemaTitle = intl.formatMessage({
    id: 'SportsPage.schemaTitle',
  });

  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [filter, setFilter] = useState(ALL);

  const getRandomSuggestions = () => {
    return sportsOptions.filter(sport => sport && sport.isFeatured); // Check for both sport and isFeatured
  };

  const useHistoryPush = (page, params = {}, search = {}) => {
    history.push(createResourceLocatorString(page, routeConfiguration(), params, search));
  };

  const handleInputChange = event => {
    const inputValue = event.target.value;
    setValue(inputValue);
    if (inputValue) {
      const filteredSuggestions = sportsOptions.filter(option =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions(getRandomSuggestions());
    }
  };

  const handleFocus = () => {
    if (!value) {
      setSuggestions(getRandomSuggestions());
    }
  };

  const handleBlur = () => {
    if (!value) {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = suggestion => {
    setValue(suggestion.label);
    const filteredSuggestions = sportsOptions.filter(option =>
      option.key.toLowerCase().includes(suggestion.key.toLowerCase())
    );
    setSuggestions(filteredSuggestions);
  };

  const handleClearInput = () => {
    setValue('');
    setSuggestions(getRandomSuggestions());
  };

  const handleFilterClick = filter => {
    setFilter(filter);
    setSuggestions([]);
    setValue('');
  };

  const filterSportsOptions = () => {
    if (filter === ALL) {
      return sportsOptions;
    }

    if (filter === OTHER) {
      return sportsOptions.filter(
        option =>
          !/^mens-/.test(option.key) &&
          !/^womens-/.test(option.key) &&
          !option?.isMens &&
          !option?.isWomens
      );
    }

    const regex = new RegExp(`^${filter}-`, 'i');
    return sportsOptions.filter(
      option =>
        regex.test(option.key) ||
        (filter === MEN && option?.isMens) ||
        (filter === WOMEN && option?.isWomens)
    );
  };

  const renderSuggestions = () => {
    if (!isArrayLength(suggestions)) {
      return null;
    }
    return (
      <ul className={css.suggestionsList}>
        {suggestions.slice(0, 8).map(suggestion => (
          <li
            key={suggestion.key}
            onClick={() => handleSuggestionClick(suggestion)}
            className={css.suggestionItem}
          >
            {suggestion.label}
          </li>
        ))}
      </ul>
    );
  };

  const renderItems = items => {
    return items.map(({ key, label, banner }) => (
      <div
        key={key}
        className={css.sliderItem}
        role="button"
        onClick={() =>
          useHistoryPush('SearchPage', null, {
            pub_pub_sports: key,
            pub_listingCategory: UserType.TALENT,
          })
        }
      >
        <div className={css.imageBlock}>
          <img
            src={banner}
            alt={label}
            className={classNames(css.sportImage, {
              [css.magnifiedLabel]: label === 'Actors/ Actresses',
            })}
          />
        </div>
        <div className={css.sportsInfo}>
          <h2>{label}</h2>
          <IconArrowTop />
        </div>
      </div>
    ));
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'SportsPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={classNames(css.content, css.sectionPadding)}>
          <div className={css.sectionHead}>
            <div className={css.leftSec}>
              <H2 as="h2" className={css.title}>
                {intl.formatMessage({ id: 'SportsPage.title' })}
              </H2>
              <p>{intl.formatMessage({ id: 'SportsPage.description' })}</p>
              <div className={css.filterButtons}>
                <button
                  onClick={() => handleFilterClick(MEN)}
                  className={classNames(css.filterButton, {
                    [css.activeFilterButton]: filter === MEN,
                  })}
                >
                  {intl.formatMessage({ id: 'SportsPage.mensSports' })}
                </button>
                <button
                  onClick={() => handleFilterClick(WOMEN)}
                  className={classNames(css.filterButton, {
                    [css.activeFilterButton]: filter === WOMEN,
                  })}
                >
                  {intl.formatMessage({ id: 'SportsPage.womensSports' })}
                </button>
                <button
                  onClick={() => handleFilterClick(OTHER)}
                  className={classNames(css.filterButton, {
                    [css.activeFilterButton]: filter === OTHER,
                  })}
                >
                  {intl.formatMessage({ id: 'SportsPage.otherSports' })}
                </button>
              </div>
            </div>
            <div className={css.rightSec}>
              <div className={css.searchBarSec}>
                <OutsideClickHandler onOutsideClick={handleBlur}>
                  <AutoSuggestion
                    handleFocus={handleFocus}
                    handleBlur={handleBlur}
                    handleInputChange={handleInputChange}
                    handleClearInput={handleClearInput}
                    value={value}
                    renderSuggestions={renderSuggestions}
                  />
                </OutsideClickHandler>
              </div>
            </div>
          </div>
          <div className={css.sportsGrid}>
            {renderItems(value === '' ? filterSportsOptions() : suggestions)}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default SportsPage;
